import React, { useEffect, useState, useRef, useCallback } from "react";
import { ListGroup, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import useOrderApi from "../../../managers/OrderManager";
import Loading, { LoadingStates } from "../../../components/Loading";
import { formatCurrency } from "../../../utilities/formatCurrency";

interface OrderListProps {
  userId?: string | null;
}

const getOrderTotal = (order: any): number => {
  const direct =
    order?.total ??
    order?.totalAmount ??
    order?.amountTotal ??
    order?.summary?.grandTotal ??
    null;

  if (typeof direct === "number") return direct;

  const items: any[] = Array.isArray(order?.lineItems) ? order.lineItems : [];
  if (!items.length) return 0;

  return items.reduce((sum, li) => {
    const qty = Number(li?.quantity ?? 0);
    const unit =
      Number(li?.price) ??
      Number(li?.unitPrice) ??
      Number(li?.amount) ??
      0;
    return sum + unit * qty;
  }, 0);
};

const getItemCount = (order: any): number => {
  const items: any[] = Array.isArray(order?.lineItems) ? order.lineItems : [];
  return items.reduce((t, li) => t + (Number(li?.quantity ?? 0) || 0), 0);
};

const OrderList: React.FC<OrderListProps> = ({ userId }) => {
  const [loadingState, setLoadingState] = useState<typeof LoadingStates[keyof typeof LoadingStates]>(LoadingStates.LOADING);
  const [loadingMessage, setLoadingMessage] = useState("Loading");
  const { getUserOrders } = useOrderApi();
  const [orders, setOrders] = useState<any[]>([]);
  const lastKeyRef = useRef<Record<string, any> | null>(null);
  const isFetching = useRef(false);
  const hasMoreOrders = useRef(true);
  const observer = useRef<IntersectionObserver | null>(null);

  const fetchOrders = useCallback(async () => {
    if (isFetching.current || !hasMoreOrders.current || !userId) return;
    isFetching.current = true;
    setLoadingState(LoadingStates.LOADING);

    try {
      const data = await getUserOrders(userId, 10, lastKeyRef.current || undefined);

      if (!data?.items?.length) {
        hasMoreOrders.current = false;
      } else {
        setOrders(prev => [...prev, ...data.items]);
        lastKeyRef.current = data.lastKey || null;
        if (!data.lastKey) hasMoreOrders.current = false;
      }

      setLoadingState(LoadingStates.SUCCESS);
    } catch (error: any) {
      console.error("Error fetching orders:", error);
      setLoadingMessage("Error fetching orders: " + (error?.message ?? String(error)));
      setLoadingState(LoadingStates.ERROR);
    } finally {
      isFetching.current = false;
    }
  }, [userId, getUserOrders]);

  useEffect(() => {
    if (orders.length === 0) fetchOrders();
  }, [fetchOrders, orders.length]);

  const lastOrderElementRef = useCallback(
    (node: Element | null) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && !isFetching.current && hasMoreOrders.current) {
            fetchOrders();
          }
        },
        { root: null, rootMargin: "200px", threshold: 0 }
      );
      if (node) observer.current.observe(node);
    },
    [fetchOrders]
  );

  if ((loadingState === LoadingStates.LOADING || loadingState === LoadingStates.ERROR) && orders.length === 0) {
    return <Loading state={loadingState} message={loadingMessage} />;
  }

  if (!orders || orders.length === 0) {
    return <div>No orders found.</div>;
  }

  return (
    <ListGroup variant="flush">
      {orders.map((order, index) => {
        const isLast = orders.length === index + 1;

        const orderId = order.customerOrderId || order.orderId || order.id;
        const placedDate = order.createdDate || order.createdAt || order.placedAt;
        const orderDate = placedDate ? new Date(placedDate) : null;
        const total = getOrderTotal(order);
        const items = getItemCount(order);

        const previewImage = order.lineItems?.[0]?.metadata?.previewImageUrls?.[0];

        return (
          <ListGroup.Item
            key={orderId ?? index}
            as={Link}
            to={`/order/${order.orderId ?? order.id}`}
            className="
              d-flex flex-row gap-3
              align-items-start align-items-md-center
              text-decoration-none
            "
            ref={isLast ? (lastOrderElementRef as unknown as React.Ref<any>) : null}
          >
            {/* Thumbnail */}
            {previewImage && (
              <img
                src={previewImage}
                alt={`Order #${orderId}`}
                style={{
                  width: 64,
                  height: 64,
                  objectFit: "cover",
                  borderRadius: "0.5rem",
                  flexShrink: 0,
                }}
              />
            )}

            {/* Info */}
            <div className="d-flex flex-column flex-grow-1 gap-1" style={{ minWidth: 0 }}>
              <div className="fw-bold text-truncate">Order #{orderId}</div>
              <div className="text-body-secondary">
                {orderDate ? `Placed on ${orderDate.toLocaleString()}` : "Date unavailable"}
              </div>
              <div className="text-body-secondary">Status: {order.status ?? "—"}</div>
            </div>

            {/* Right side: total + items */}
            <div className="ms-auto d-flex flex-column align-items-end">
              <div className="fw-semibold">{formatCurrency(total)}</div>
              <div className="text-body-secondary">
                {items} item{items === 1 ? "" : "s"}
              </div>
            </div>
          </ListGroup.Item>
        );
      })}

      {loadingState === LoadingStates.LOADING && (
        <ListGroup.Item className="text-center">
          <Loading state={LoadingStates.LOADING} message="Loading more orders..." />
        </ListGroup.Item>
      )}
    </ListGroup>
  );
};

export default OrderList;
